import { useMemo, useState } from 'react';
import { allMovies, genreList } from '@/mocks/movies';
import MovieCard from '@/components/feature/MovieCard';
import SectionHeading from '@/pages/home/components/SectionHeading';

export default function GenreSection() {
  const [activeGenre, setActiveGenre] = useState('全部');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return allMovies.filter((movie) => {
      const matchGenre = activeGenre === '全部' || movie.genre === activeGenre;
      if (!matchGenre) return false;
      if (!keyword) return true;
      const haystack = [movie.title, movie.director, movie.cast, movie.region, ...movie.tags]
        .join(' ')
        .toLowerCase();
      return haystack.includes(keyword);
    });
  }, [activeGenre, query]);

  return (
    <section id="genres" className="w-full bg-background-100 py-14 md:py-20">
      <div className="mx-auto w-full max-w-[1200px] px-4 md:px-8">
        <SectionHeading
          eyebrow="分类找片"
          title="按类型与关键词，快速缩小范围"
          subtitle="先点类型标签，再输入片名、标签或主演名做二次筛选。列表会实时更新，并显示当前匹配到的影片数量。"
        />

        <div className="mt-8 flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            {genreList.map((genre) => {
              const count = genre === '全部' ? allMovies.length : allMovies.filter((m) => m.genre === genre).length;
              const isActive = activeGenre === genre;
              return (
                <button
                  key={genre}
                  type="button"
                  onClick={() => setActiveGenre(genre)}
                  aria-pressed={isActive}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-2 font-label text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? 'border-primary-600 bg-primary-600 text-background-50'
                      : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {genre}
                  <span
                    className={`font-label text-xs ${isActive ? 'text-background-100' : 'text-foreground-400'}`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-sm">
              <i
                className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"
                aria-hidden="true"
              />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="搜索片名、标签或主演…"
                aria-label="搜索影片"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 font-label text-sm text-foreground-900 outline-none transition-colors duration-200 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <p className="font-label text-xs text-foreground-500 md:text-sm">
              共找到{' '}
              <strong className="font-bold text-primary-600">{filtered.length}</strong> 部影片
              {activeGenre !== '全部' ? ` · ${activeGenre}` : ''}
              {query.trim() ? ` · 关键词「${query.trim()}」` : ''}
            </p>
          </div>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
            {filtered.map((movie) => (
              <MovieCard key={movie.id} movie={movie} compact />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400" aria-hidden="true" />
            <p className="font-heading text-lg font-bold text-foreground-800">没有找到匹配的影片</p>
            <p className="text-sm text-foreground-500">试试换个类型标签，或清空搜索关键词再找一次。</p>
            <button
              type="button"
              onClick={() => {
                setActiveGenre('全部');
                setQuery('');
              }}
              className="mt-1 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 font-label text-sm font-semibold text-background-50 transition-colors duration-200 hover:bg-primary-700"
            >
              重置筛选条件
            </button>
          </div>
        )}
      </div>
    </section>
  );
}